import { cn } from '@/lib/utils';
import { useSidebarStore } from '@/stores/sidebar.store';
import { useAuthStore } from '@/stores/auth.store';
import { getScopedProfile, isPathAllowed } from '@/lib/scopedAccess';
import { isPathModuleEnabled } from '@/lib/modules';
import {
  BarChart3,
  Bell,
  Calendar,
  ChevronLeft,
  ChevronDown,
  CreditCard,
  FileSearch,
  FileText,
  Gauge,
  Globe,
  HelpCircle,
  History,
  Key,
  LayoutDashboard,
  ListChecks,
  Activity,
  ScrollText,
  ArrowUpDown,
  MapPin,
  Monitor,
  Play,
  Radio,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  MessageCircle,
  Target,
  Timer,
  UserPlus,
  Users,
  Wallet,
  ReceiptIndianRupee,
  SlidersHorizontal,
  Webhook,
  Wifi,
  Upload,
  Wrench,
  Network,
  Database,
  HardDrive,
  Receipt,
  RefreshCw,
  Scale,
  TrendingUp,
  Megaphone,
  Server,
  AlertOctagon,
  X,
  HandCoins,
  Boxes,
  Layers,
  ShoppingCart,
  ClipboardCheck,
  Router,
  CircleX,
  BadgeIndianRupee,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { getInitials } from '@/lib/utils';
import { prefetchRoute } from '@/lib/prefetch';

interface NavItem {
  tKey: string;
  href?: string;
  icon: React.ElementType;
  children?: NavItem[];
}

interface NavSection {
  tKey: string;
  items: NavItem[];
}

/**
 * The menu, as three clusters of collapsible categories.
 *
 * Reorganised 2026-09-16 at the operator's request. What it fixes: every one of the twelve old sections
 * showed all of its children at once, so the sidebar was 102 rows in a column that fits about twenty, and
 * "Network" alone was fifteen unrelated items — topology, RADIUS, alerts, CPE stock, IP pools, firmware.
 *
 * Rules it follows, so later additions land in the right place:
 *  - A category is collapsed until you open it. At rest this is nineteen rows and never scrolls.
 *  - Daily work (subscribers, billing, support) sits above the engineering categories.
 *  - Anything you configure once lives in the Settings hub, which already groups it into cards. That is
 *    why fifteen administration links are gone from here and only `Settings` remains.
 *  - Similar names that are NOT the same page keep both, disambiguated: `/webhooks` is the tenant's own
 *    outgoing webhooks, `/api-management/webhooks` is a partner's. Merging them would have removed a page.
 */
const navSections: NavSection[] = [
  {
    // Dashboard leads this cluster rather than sitting under a heading of its own: a section header for a
    // single link costs two rows, and the whole point of this layout is that nothing has to scroll.
    tKey: 'sidebar.secOperations',
    items: [
      { tKey: 'sidebar.dashboard', href: '/dashboard', icon: LayoutDashboard },
      {
        tKey: 'sidebar.crm', icon: Target, children: [
          { tKey: 'sidebar.leadDashboard', href: '/leads/dashboard', icon: Gauge },
          { tKey: 'sidebar.allLeads', href: '/leads', icon: FileText },
          { tKey: 'sidebar.addLead', href: '/leads/new', icon: UserPlus },
          { tKey: 'sidebar.campaigns', href: '/campaigns', icon: Megaphone },
        ],
      },
      {
        tKey: 'sidebar.subscribers', icon: Users, children: [
          { tKey: 'sidebar.subscriberDashboard', href: '/subscribers/dashboard', icon: BarChart3 },
          { tKey: 'sidebar.subscriberList', href: '/subscribers', icon: Users },
          { tKey: 'sidebar.onboarding', href: '/subscriber-onboarding', icon: UserPlus },
          { tKey: 'sidebar.installationQueue', href: '/subscriber-onboarding/installations', icon: ListChecks },
          { tKey: 'sidebar.staticIpRequests', href: '/subscribers/static-ip-requests', icon: Globe },
          { tKey: 'sidebar.plans', href: '/plans', icon: ListChecks },
        ],
      },
      {
        tKey: 'sidebar.billing', icon: CreditCard, children: [
          { tKey: 'sidebar.invoicesBilling', href: '/billing', icon: CreditCard },
          { tKey: 'sidebar.collections', href: '/billing/collections', icon: HandCoins },
          { tKey: 'sidebar.paymentsReceived', href: '/billing/payments/received', icon: BadgeIndianRupee },
          { tKey: 'sidebar.failedPayments', href: '/billing/payments/failed', icon: CircleX },
          { tKey: 'sidebar.paymentGateway', href: '/billing/gateway', icon: Wallet },
          { tKey: 'sidebar.autopay', href: '/billing/autopay', icon: RefreshCw },
          { tKey: 'sidebar.paymentReminders', href: '/billing/reminders', icon: Bell },
          { tKey: 'sidebar.reconciliation', href: '/billing/reconciliation', icon: Scale },
          { tKey: 'sidebar.taxGst', href: '/billing/tax', icon: Receipt },
          { tKey: 'sidebar.expenses', href: '/expenses', icon: Receipt },
          { tKey: 'sidebar.billingAutomation', href: '/billing/automation', icon: Timer },
          { tKey: 'sidebar.invoiceTemplates', href: '/billing/templates', icon: FileText },
        ],
      },
      {
        tKey: 'sidebar.support', icon: HelpCircle, children: [
          { tKey: 'sidebar.tickets', href: '/tickets', icon: HelpCircle },
          { tKey: 'sidebar.slaPolicies', href: '/sla', icon: Timer },
          {
            tKey: 'sidebar.whatsapp', icon: MessageCircle, children: [
              { tKey: 'sidebar.whatsappInbox', href: '/whatsapp/inbox', icon: MessageCircle },
              { tKey: 'sidebar.whatsappTemplates', href: '/settings/whatsapp/templates', icon: FileText },
              { tKey: 'sidebar.whatsappAutomation', href: '/settings/whatsapp/automation', icon: Timer },
              { tKey: 'sidebar.whatsappAnalytics', href: '/settings/whatsapp/analytics', icon: BarChart3 },
            ],
          },
          {
            tKey: 'sidebar.notifications', icon: Bell, children: [
              { tKey: 'sidebar.allNotifications', href: '/notifications', icon: Bell },
              { tKey: 'sidebar.bulkSend', href: '/notifications/bulk', icon: Send },
              { tKey: 'sidebar.templates', href: '/notifications/templates', icon: FileText },
              { tKey: 'sidebar.emailTemplates', href: '/notifications/email-templates', icon: FileText },
            ],
          },
        ],
      },
      {
        // Field Ops, phase 1 of the approved redesign: Dispatch is now ONE workspace (queue + map +
        // crew) instead of four destinations. The screens it supersedes stay reachable under
        // "Classic views" until the workspace has run a few real shifts — nothing loses its entry
        // point, which is the same rule the sidebar reorganisation was held to.
        tKey: 'sidebar.fieldOps', icon: Radio, children: [
          { tKey: 'sidebar.foDispatchWorkspace', href: '/dispatch', icon: Radio },
          // Employees and Technicians listed the same people; they are one roster now, and route
          // history lives on the technician it describes. All three pages still resolve.
          { tKey: 'sidebar.foWorkforce', href: '/dispatch/workforce', icon: Users },
          // Reports and the Leaderboard are tabs of Insights. SLA/shift config and Geofences are
          // configuration, so they live in the Settings hub with everything else you set once — the same
          // move Administration made. Van stock left Field Ops on 2026-09-18: technicians hold material only
          // as custody for a job (Inventory › Job Materials), so the old Resources page is just approvals.
          { tKey: 'sidebar.expenseApprovals', href: '/dispatch/expenses', icon: Receipt },
          { tKey: 'sidebar.foInsights', href: '/dispatch/insights', icon: BarChart3 },
          {
            // Every screen the redesign supersedes, kept reachable until the new ones have run real
            // shifts. Removing this group is the last step, and it is the operator's call.
            tKey: 'sidebar.foClassic', icon: History, children: [
              { tKey: 'sidebar.commandCenter', href: '/dispatch/command', icon: LayoutDashboard },
              { tKey: 'sidebar.dispatchBoard', href: '/dispatch/board', icon: Radio },
              { tKey: 'sidebar.dispatchMap', href: '/dispatch/map', icon: MapPin },
              { tKey: 'sidebar.outageConsole', href: '/dispatch/outages', icon: AlertOctagon },
              { tKey: 'sidebar.employees', href: '/dispatch/employees', icon: Users },
              { tKey: 'sidebar.technicians', href: '/dispatch/technicians', icon: Wrench },
              { tKey: 'sidebar.technicianHistory', href: '/dispatch/history', icon: History },
            ],
          },
        ],
      },
      {
        // Inventory, its own module since 2026-09-17. The approved design turned 18 menu items into six
        // sections (Dashboard, Job Materials, Stock, Purchasing, Assets, Reports) with the pages as tabs; each
        // section appears here when it ships. Item Master and the rules live in the Settings hub. Assets replaced
        // the Classic CPE screen (Network › Network Assets) on 2026-09-18; Reports completed the six on the same day.
        tKey: 'sidebar.inventory', icon: Boxes, children: [
          { tKey: 'sidebar.invDashboard', href: '/inventory', icon: LayoutDashboard },
          { tKey: 'sidebar.invJobs', href: '/inventory/jobs', icon: ClipboardCheck },
          { tKey: 'sidebar.invStock', href: '/inventory/stock', icon: Layers },
          { tKey: 'sidebar.invPurchasing', href: '/inventory/purchasing', icon: ShoppingCart },
          { tKey: 'sidebar.invAssets', href: '/inventory/assets', icon: HardDrive },
          { tKey: 'sidebar.invReports', href: '/inventory/reports', icon: BarChart3 },
        ],
      },
    ],
  },
  {
    tKey: 'sidebar.secNetwork',
    items: [
      {
        tKey: 'sidebar.nocAlerts', icon: Monitor, children: [
          { tKey: 'sidebar.noc', href: '/noc', icon: Monitor },
          { tKey: 'sidebar.alertRules', href: '/noc/alert-rules', icon: Shield },
          { tKey: 'sidebar.alertHistory', href: '/noc/history', icon: History },
        ],
      },
      {
        tKey: 'sidebar.nasRadius', icon: Server, children: [
          { tKey: 'sidebar.nasManagement', href: '/network/nas', icon: Server },
          { tKey: 'sidebar.liveSessions', href: '/network/sessions', icon: Activity },
          { tKey: 'sidebar.pppoeManagement', href: '/radius/pppoe', icon: Wifi },
          { tKey: 'sidebar.radiusManager', href: '/radius-manager', icon: Server },
          { tKey: 'sidebar.nasFailover', href: '/nas/failover', icon: RefreshCw },
        ],
      },
      {
        tKey: 'sidebar.accessNetwork', icon: Radio, children: [
          { tKey: 'sidebar.oltOverview', href: '/olt/overview', icon: LayoutDashboard },
          { tKey: 'sidebar.olts', href: '/olt', icon: Radio },
          { tKey: 'sidebar.allOnus', href: '/olt/onus', icon: Router },
          { tKey: 'sidebar.oltAnalytics', href: '/olt/analytics', icon: BarChart3 },
          { tKey: 'sidebar.wifiReadiness', href: '/olt/wifi-readiness', icon: Gauge },
          { tKey: 'sidebar.onuMovements', href: '/olt/movement-history', icon: History },
          { tKey: 'sidebar.duplicateOnu', href: '/olt/duplicate-onu', icon: AlertOctagon },
        ],
      },
      {
        tKey: 'sidebar.ipBandwidth', icon: Globe, children: [
          { tKey: 'sidebar.ipManagement', href: '/ipam', icon: Globe },
          { tKey: 'sidebar.bandwidthFup', href: '/bandwidth', icon: Gauge },
        ],
      },
      {
        tKey: 'sidebar.networkAssets', icon: Network, children: [
          { tKey: 'sidebar.topologyMap', href: '/network/topology', icon: Network },
          { tKey: 'sidebar.firmwareUpdates', href: '/firmware', icon: Upload },
          { tKey: 'sidebar.firmwareLibrary', href: '/settings/firmware-library', icon: HardDrive },
          { tKey: 'sidebar.maintenance', href: '/maintenance', icon: Wrench },
          { tKey: 'sidebar.networkManagement', href: '/network/management', icon: Shield },
        ],
      },
    ],
  },
  {
    tKey: 'sidebar.secPlatform',
    items: [
      {
        tKey: 'sidebar.partnerManagement', icon: HandCoins, children: [
          { tKey: 'sidebar.partnerDashboard', href: '/partners/dashboard', icon: BarChart3 },
          { tKey: 'sidebar.partners', href: '/partners', icon: Users },
          { tKey: 'sidebar.partnerOperations', href: '/partners/operations', icon: HandCoins },
          { tKey: 'sidebar.commissionReport', href: '/partners/commission-report', icon: ReceiptIndianRupee },
          { tKey: 'sidebar.commissionSettings', href: '/settings/partner-commission', icon: SlidersHorizontal },
          // A partner's own home. Filtered out for staff below; for a partner login it is the only item.
          { tKey: 'sidebar.partnerPortal', href: '/partner-portal', icon: LayoutDashboard },
        ],
      },
      {
        tKey: 'sidebar.ipdrCompliance', icon: ShieldCheck, children: [
          { tKey: 'sidebar.ipdrDashboard', href: '/ipdr', icon: ShieldCheck },
          { tKey: 'sidebar.ipdrSearch', href: '/ipdr/search', icon: FileSearch },
          { tKey: 'sidebar.ipdrCases', href: '/ipdr/cases', icon: Scale },
          { tKey: 'sidebar.ipdrServer', href: '/ipdr/server', icon: Activity },
          { tKey: 'sidebar.ipdrStorage', href: '/ipdr/storage-analytics', icon: HardDrive },
          { tKey: 'sidebar.ipdrIngestion', href: '/ipdr/ingestion', icon: Radio },
          { tKey: 'sidebar.ipdrAudit', href: '/ipdr/audit', icon: ScrollText },
        ],
      },
      {
        tKey: 'sidebar.integrations', icon: Webhook, children: [
          {
            // The tenant's own API surface — not the same pages as the partner ones below.
            tKey: 'sidebar.platformApis', icon: Key, children: [
              { tKey: 'sidebar.apiKeys', href: '/api-keys', icon: Key },
              { tKey: 'sidebar.apiUsage', href: '/api-usage', icon: BarChart3 },
              { tKey: 'sidebar.outgoingWebhooks', href: '/webhooks', icon: Webhook },
            ],
          },
          {
            tKey: 'sidebar.partnerApis', icon: Network, children: [
              { tKey: 'sidebar.apiPartners', href: '/api-management/partners', icon: Network },
              { tKey: 'sidebar.apiCredentials', href: '/api-management/credentials', icon: Key },
              { tKey: 'sidebar.apiServiceMapping', href: '/api-management/service-mapping', icon: Network },
              { tKey: 'sidebar.apiWebhooks', href: '/api-management/webhooks', icon: Webhook },
              { tKey: 'sidebar.apiHealth', href: '/api-management/health', icon: Activity },
              { tKey: 'sidebar.apiLogs', href: '/api-management/logs', icon: ScrollText },
              { tKey: 'sidebar.apiAudit', href: '/api-management/audit', icon: FileSearch },
            ],
          },
        ],
      },
      {
        tKey: 'sidebar.analytics', icon: TrendingUp, children: [
          { tKey: 'sidebar.revenueAnalytics', href: '/analytics', icon: Activity },
          { tKey: 'sidebar.advancedAnalytics', href: '/analytics/advanced', icon: TrendingUp },
          { tKey: 'sidebar.reports', href: '/reports', icon: BarChart3 },
          { tKey: 'sidebar.reportBuilder', href: '/reports/builder', icon: SlidersHorizontal },
          { tKey: 'sidebar.businessIntelligence', href: '/reports/bi', icon: TrendingUp },
          { tKey: 'sidebar.scheduledReports', href: '/scheduled-reports', icon: Calendar },
        ],
      },
      {
        // Running the platform, as opposed to configuring it: what is happening now and what happened.
        tKey: 'sidebar.system', icon: Activity, children: [
          { tKey: 'sidebar.systemHealth', href: '/system-health', icon: Activity },
          { tKey: 'sidebar.jobsQueues', href: '/jobs', icon: Play },
          { tKey: 'sidebar.auditTrail', href: '/audit', icon: FileSearch },
          { tKey: 'sidebar.logs', href: '/logs', icon: FileText },
          { tKey: 'sidebar.liveLogs', href: '/logs/live', icon: Radio },
          { tKey: 'sidebar.importExport', href: '/import-export', icon: ArrowUpDown },
          { tKey: 'sidebar.backupRestore', href: '/settings/backup', icon: Database },
          { tKey: 'sidebar.dataCompliance', href: '/compliance', icon: Shield },
        ],
      },
      // No Settings entry here: the sidebar footer already pins one, and two links to the same page in one
      // menu is the confusion this reorganisation set out to remove.
    ],
  },
];

/** Every leaf under an item, however deep. A category's pages, flattened. */
function leavesOf(item: NavItem): NavItem[] {
  if (item.children) return item.children.flatMap(leavesOf);
  return item.href ? [item] : [];
}

/**
 * Keep only the leaves `allow` accepts, then drop any group left with nothing in it — at EVERY depth.
 *
 * This has to recurse. The filters it replaces looked one level down, which was correct while the menu
 * was two deep; now that a category can hold a sub-section, a one-level filter would leave every
 * grandchild unfiltered — and for the scoped-role filter that means showing a restricted user menu
 * items they are not allowed to open.
 */
function pruneNav(items: NavItem[], allow: (href: string) => boolean): NavItem[] {
  return items
    .map((it) => (it.children ? { ...it, children: pruneNav(it.children, allow) } : it))
    .filter((it) => (it.children ? it.children.length > 0 : it.href ? allow(it.href) : false));
}

/** Every href declared anywhere in the nav, used for the longest-match rule below. */
const ALL_NAV_HREFS: string[] = navSections.flatMap((s) =>
  s.items.flatMap((i) => (i.href ? [i.href] : (i.children ?? []).map((c) => c.href).filter((h): h is string => !!h))),
);

/**
 * Is `href` the nav entry that represents `pathname`?
 *
 * Prefix matching alone lights up every ancestor: on /leads/dashboard the entry for /leads
 * ("All Leads") also matched, so two items appeared selected at once. A nav entry therefore only
 * wins if NO other declared entry is a longer (more specific) match for the same path — the
 * standard longest-prefix rule. /leads/dashboard beats /leads, /leads/new beats /leads, and landing
 * on /leads itself still lights All Leads because nothing longer matches.
 */
function isHrefActive(pathname: string, href?: string) {
  if (!href) return false;
  // '/dispatch' is the board; its sub-routes aren't nav entries, so match it exactly — except a job's own
  // page, which belongs to the workspace it was opened from.
  if (href === '/dispatch') return pathname === '/dispatch' || pathname.startsWith('/dispatch/jobs/');
  const matches = (h: string) => pathname === h || pathname.startsWith(h + '/');
  if (!matches(href)) return false;
  return !ALL_NAV_HREFS.some((h) => h !== href && h.length > href.length && matches(h));
}

function NavLeaf({ item, isCollapsed, pathname, t, nested }: { item: NavItem; isCollapsed: boolean; pathname: string; t: TFunction; nested?: boolean }) {
  const isActive = isHrefActive(pathname, item.href);
  const label = t(item.tKey);
  return (
    <li>
      <Link
        to={item.href!}
        onMouseEnter={() => prefetchRoute(item.href!)}
        onFocus={() => prefetchRoute(item.href!)}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'group relative flex items-center gap-3 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-all duration-150',
          isActive ? 'bg-primary/10 text-primary' : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          isCollapsed && 'justify-center px-0',
          nested && !isCollapsed && 'py-1.5 text-[13px]',
        )}
        title={isCollapsed ? label : undefined}
      >
        {isActive && !isCollapsed && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />}
        <item.icon className={cn('shrink-0 transition-colors', nested ? 'h-4 w-4' : 'h-[18px] w-[18px]', isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')} />
        {!isCollapsed && <span className="truncate">{label}</span>}
      </Link>
    </li>
  );
}

/**
 * A category in the collapsed rail: one icon, with its pages in a flyout on hover or keyboard focus.
 *
 * The rail used to flatten each category to its leaves, which was fine while most entries were single
 * links. After the reorganisation every entry is a category, so that flattening put 102 icons in a
 * 72px strip — a scrolling column of undifferentiated glyphs. One icon per category gives the rail
 * back its point (16 icons, nothing scrolls) and the flyout keeps every page one hover away, so
 * collapsing the sidebar costs you no navigation.
 */
function RailGroup({ item, pathname, t }: { item: NavItem; pathname: string; t: TFunction }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLLIElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const childActive = leavesOf(item).some((c) => isHrefActive(pathname, c.href));
  const label = t(item.tKey);
  const children = item.children ?? [];

  // Sit beside the icon, then pull back up if the panel would run off the bottom — the last category
  // in the rail holds as many pages as the first. useLayoutEffect so the correction lands before
  // paint; with useEffect the panel visibly jumps.
  useLayoutEffect(() => {
    if (!open) return;
    const a = anchorRef.current?.getBoundingClientRect();
    const p = panelRef.current?.getBoundingClientRect();
    if (!a || !p) return;
    const margin = 8;
    setPos({ top: Math.max(margin, Math.min(a.top, window.innerHeight - margin - p.height)), left: a.right });
  }, [open]);

  // Navigating away should not leave a flyout hanging over the new page.
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <li
      ref={anchorRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false); }}
      onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'group flex w-full items-center justify-center rounded-lg py-1.5 transition-all duration-150',
          childActive ? 'bg-primary/10 text-primary' : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        )}
      >
        <item.icon className={cn('h-[18px] w-[18px] shrink-0 transition-colors', childActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')} />
      </button>

      {open && (
        // Fixed, not absolute: the rail is an overflow-y-auto column, and a panel taller than its own
        // icon would be clipped by it. The wrapper's left padding is the gap to the rail, so the
        // pointer never crosses dead space on its way across and the flyout doesn't flicker shut.
        <div
          ref={panelRef}
          style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden' }}
          className="fixed z-[60] pl-2"
        >
          <div className="max-h-[80vh] w-56 overflow-y-auto rounded-lg border bg-popover p-1.5 shadow-lg">
            <p className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{label}</p>
            <ul className="space-y-0.5">
              {children.map((c) => (c.children
                ? (
                  <li key={c.tKey}>
                    <p className="mt-1 px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">{t(c.tKey)}</p>
                    <ul className="ml-1 space-y-0.5 border-l pl-1.5">
                      {c.children.map((g) => <NavLeaf key={g.href} item={g} isCollapsed={false} pathname={pathname} t={t} nested />)}
                    </ul>
                  </li>
                )
                : <NavLeaf key={c.href} item={c} isCollapsed={false} pathname={pathname} t={t} nested />))}
            </ul>
          </div>
        </div>
      )}
    </li>
  );
}

function NavGroup({
  item, isCollapsed, pathname, t, open, onToggle,
}: {
  item: NavItem; isCollapsed: boolean; pathname: string; t: TFunction;
  open: boolean; onToggle: () => void;
}) {
  const children = item.children ?? [];
  const childActive = leavesOf(item).some((c) => isHrefActive(pathname, c.href));

  // The rail is one icon per category, with the pages behind a flyout. See RailGroup.
  if (isCollapsed) {
    return <RailGroup item={item} pathname={pathname} t={t} />;
  }

  const label = t(item.tKey);
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          'group flex w-full items-center gap-3 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-all duration-150',
          childActive ? 'text-primary' : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        )}
      >
        <item.icon className={cn('h-[18px] w-[18px] shrink-0 transition-colors', childActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')} />
        <span className="flex-1 truncate text-left">{label}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="ml-[18px] mt-0.5 space-y-0.5 border-l pl-2">
          {children.map((c) => (c.children
            ? <NavSubGroup key={c.tKey} item={c} pathname={pathname} t={t} />
            : <NavLeaf key={c.href} item={c} isCollapsed={false} pathname={pathname} t={t} nested />))}
        </ul>
      )}
    </li>
  );
}

/**
 * A section inside an open category — WhatsApp under Support, NAS & RADIUS under Network.
 *
 * Its open state is its own rather than part of the single-open accordion above: that rule exists so two
 * top-level categories can't be open at once, and applying it here would collapse the section you are
 * standing in as soon as you opened a sibling. It opens itself whenever the page you are on lives inside it.
 */
function NavSubGroup({ item, pathname, t }: { item: NavItem; pathname: string; t: TFunction }) {
  const active = leavesOf(item).some((c) => isHrefActive(pathname, c.href));
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);
  const label = t(item.tKey);

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-all duration-150',
          active ? 'text-primary' : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        )}
      >
        <item.icon className={cn('h-4 w-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')} />
        <span className="flex-1 truncate text-left">{label}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="ml-[15px] mt-0.5 space-y-0.5 border-l pl-2">
          {(item.children ?? []).map((c) => (
            <NavLeaf key={c.href} item={c} isCollapsed={false} pathname={pathname} t={t} nested />
          ))}
        </ul>
      )}
    </li>
  );
}

export function Sidebar() {
  const { isCollapsed, toggle, isMobileOpen, closeMobile } = useSidebarStore();
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const { t } = useTranslation();

  /**
   * Accordion: at most one parent menu is expanded at a time.
   *
   * Held here rather than inside each NavGroup so opening one can close the others — with the
   * state per-group, Leads stayed expanded underneath Subscribers. Three states:
   *   undefined — follow the route (the group owning the current page is open, all others closed)
   *   null      — the user collapsed the open group, so none are
   *   <tKey>    — the user opened that group explicitly
   * Navigating resets to route-driven, which is what keeps Subscribers expanded (and the right
   * child highlighted) on every subscriber page.
   */
  const [manualGroup, setManualGroup] = useState<string | null | undefined>(undefined);
  useEffect(() => { setManualGroup(undefined); }, [location.pathname]);

  const groupHasActiveChild = (item: NavItem) =>
    leavesOf(item).some((c) => isHrefActive(location.pathname, c.href));
  const isGroupOpen = (item: NavItem) =>
    manualGroup === undefined ? groupHasActiveChild(item) : manualGroup === item.tKey;
  const toggleGroup = (item: NavItem) =>
    setManualGroup(isGroupOpen(item) ? null : item.tKey);

  // Restricted (scoped) roles see only their allowed sections; all normal roles see everything.
  const scoped = getScopedProfile(user?.permissions, user?.partnerId);
  // The partner home only makes sense for a partner's own login; staff reach partners from Partners.
  const baseSections = user?.partnerId
    ? navSections
    // It sits inside Partners now, so this has to reach in rather than filter the top level.
    : navSections
        .map((s) => ({ ...s, items: pruneNav(s.items, (href) => href !== '/partner-portal') }))
        .filter((s) => s.items.length > 0);
  const scopedSections = scoped
    ? baseSections
        .map((s) => ({ ...s, items: pruneNav(s.items, (href) => isPathAllowed(href, scoped)) }))
        .filter((s) => s.items.length > 0)
    : baseSections;

  // Per-tenant module entitlement filter (composes with the scoped filter above).
  const enabledModules = user?.enabledModules;
  const sections = scopedSections
    .map((s) => ({ ...s, items: pruneNav(s.items, (href) => isPathModuleEnabled(enabledModules, href)) }))
    .filter((s) => s.items.length > 0);
  const showSettings = !scoped || isPathAllowed('/settings', scoped);

  // Close mobile sidebar on route change
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Close mobile sidebar on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileOpen) closeMobile();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isMobileOpen, closeMobile]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={closeMobile}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r bg-sidebar transition-all duration-200 ease-in-out',
          // Desktop: normal sidebar behavior
          'max-lg:w-sidebar',
          isCollapsed ? 'lg:w-sidebar-collapsed' : 'lg:w-sidebar',
          // Mobile: slide in/out
          isMobileOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full'
        )}
      >
      {/* Logo */}
      <div className="flex h-14 items-center gap-3 border-b px-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary">
          <Globe className="h-4 w-4 text-white" />
        </div>
        {!isCollapsed && (
          <div className="flex flex-col overflow-hidden">
            <span className="truncate text-sm font-semibold text-foreground">ISP Portal</span>
            <span className="truncate text-2xs text-muted-foreground">{t('sidebar.managementPlatform')}</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav aria-label="Main navigation" className="scrollbar-thin flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section, si) => (
          <div key={section.tKey} className="mb-3">
            {/* The rail has no room for the section name, but it still keeps the three clusters apart. */}
            {isCollapsed
              ? si > 0 && <div className="mx-2 mb-2 h-px bg-border" />
              : (
                <p className="mb-1.5 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  {t(section.tKey)}
                </p>
              )}
            <ul className="space-y-0.5">
              {section.items.map((item) => (item.children
                ? (
                  <NavGroup
                    key={item.tKey}
                    item={item}
                    isCollapsed={isCollapsed}
                    pathname={location.pathname}
                    t={t}
                    open={isGroupOpen(item)}
                    onToggle={() => toggleGroup(item)}
                  />
                )
                : <NavLeaf key={item.href} item={item} isCollapsed={isCollapsed} pathname={location.pathname} t={t} />))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Settings link */}
      {showSettings && (
      <div className="border-t px-3 py-2">
        <Link
          to="/settings"
          className={cn(
            'group flex items-center gap-3 rounded-lg px-2.5 py-1.5 text-sm font-medium text-sidebar-foreground transition-all hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            location.pathname.startsWith('/settings') && 'bg-primary/10 text-primary',
            isCollapsed && 'justify-center px-0'
          )}
          title={isCollapsed ? t('sidebar.settings') : undefined}
        >
          <Settings className="h-[18px] w-[18px] shrink-0 text-muted-foreground group-hover:text-foreground" />
          {!isCollapsed && <span>{t('sidebar.settings')}</span>}
        </Link>
      </div>
      )}

      {/* User info */}
      <div className="border-t px-3 py-3">
        <div
          className={cn(
            'flex items-center gap-3',
            isCollapsed && 'justify-center'
          )}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {user ? getInitials(`${user.firstName} ${user.lastName}`) : 'U'}
          </div>
          {!isCollapsed && user && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{user.firstName} {user.lastName}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile close button */}
      <button
        onClick={closeMobile}
        className="absolute right-3 top-3.5 flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent lg:hidden"
        aria-label="Close sidebar"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Desktop collapse toggle */}
      <button
        onClick={toggle}
        className="absolute -right-3 top-20 hidden h-6 w-6 items-center justify-center rounded-full border bg-background shadow-sm transition-colors hover:bg-accent lg:flex"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ChevronLeft
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200',
            isCollapsed && 'rotate-180'
          )}
        />
      </button>
    </aside>
    </>
  );
}
